import { defineComponent } from 'vue'
import { Community } from '@/Domain/Community/types'
import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '@/store/modules/filters'
import { requestAccessToCommunity } from '@/api/communities'
import { ACTION_TYPES } from '@/store/modules/user'
import UserMixin from '@/util/UserMixin'

export default defineComponent({
  mixins: [UserMixin],
  computed: {
    accessLevelOptions () {
      return [
        { value: 'any_user', label: this.$t('community_accessibility_any_registered_user') },
        { value: 'user_with_claims', label: this.$t('community_accessibility_users_with_claimed_profile') },
      ]
    },
    visibilityOptions () {
      return [
        { value: 'public', label: this.$t('community_visibility_public') },
        { value: 'private', label: this.$t('community_visibility_private') },
      ]
    },
    doesUserHaveFullAccessToCommunity (): boolean {
      if (!this.$store.getters.isLoggedIn) {
        return false
      }

      if (this.isMember) {
        return true
      }

      if (this.isLoggedInUserCommunityManager) {
        return true
      }

      return this.$store.getters.joinedCommunityIds.includes(this.community?.id) && this.hasCommunityAccessBasedOnAccessLevel(this.community)
    },
    displayRequestAccessButton (): boolean {
      if (this.isMember) {
        return false
      }

      return !this.$store.getters.joinedCommunityIds.includes(this.community?.id)
    },
    hasUserRequestedAccessToCommunity (): boolean {
      return this.$store.getters.requestedToJoinCommunityIds.includes(this.community?.id)
    },
    hasAccessToCommunities (): boolean {
      return this.$store.getters.hasAccessToCommunities
    },
    hasAccessToAnnouncementsAndEventsForCommunities (): boolean {
      return this.$store.getters.hasAccessToAnnouncementsAndEventsForCommunities
    },
    canCreateAnnouncement (): boolean {
      return this.doesUserHaveFullAccessToCommunity && this.$store.getters.canCreateAnnouncement
    },
    displayCommunityActors (): boolean {
      return (this.community.enableActors ?? true) && this.doesUserHaveFullAccessToCommunity
    },
    displayCommunityKnowledgeBase (): boolean {
      return (this.config?.viewDashboard && this.config.viewDashboard.includes('knowledge-base')) && this.community.enableKnowledgeBase && this.doesUserHaveFullAccessToCommunity
    },
    displayCommunityAnnouncements (): boolean {
      return this.$store.getters.canUsePublicAnnouncements && this.community.enableAnnouncementsAndEvents && this.doesUserHaveFullAccessToCommunity
    },
    areChallengesEnabled (): boolean {
      return this.$store.getters.areChallengesEnabled
    },
    displayCommunityChallenges (): boolean {
      return (
        this.$store.getters.canCreateChallenge ||
        this.$store.getters.viewableChallengeStatusses.length > 0 ||
        this.$store.getters.accessibleChallengeStatusses.length > 0
      ) && this.community.enableChallenges && this.doesUserHaveFullAccessToCommunity
    },
    canUserManageMembers (): boolean {
      return this.isMember || this.isLoggedInUserCommunityManager
    },
    isLoggedInUserCommunityManager (): boolean {
      if (this.isMember) {
        return true
      }

      if (this.community?.communityManagerIds) {
        return this.community.communityManagerIds.includes(Number(this.$store.getters.userId))
      }
      // Fallback for backward compatibility
      return Number(this.community?.communityManager?.id) === Number(this.$store.getters.userId)
    },
    isMember (): boolean {
      return this.$store.getters.isMember
    },
    // Return all communities the user is either a verified member of or a community manager of
    joinedCommunities (): Community[] {
      return this.$store.state.communities.listData.data.filter((community: any) => {
        return this.$store.getters.joinedCommunityIds.includes(community.id)
      })
    },
    communityId (): Number {
      return Number(this.$route.params.communityId)
    },
    communities (): Community[] {
      return this.$store.state.communities.listData.data
    },
  },
  methods: {
    getCommunity (): Community | undefined {
      return this.communities.find((community: Community) => community.id === this.communityId)
    },
    hasCommunityAccessBasedOnAccessLevel (community: Community): boolean {
      if (this.isMember) {
        return true
      }

      if (community.accessLevel !== 'user_with_claims') {
        return true
      }

      return this.$store.getters.userClaims.length > 0
    },
    isCommunityManager (community: Community): boolean {
      if (community.communityManagerIds) {
        return community.communityManagerIds.includes(Number(this.$store.getters.userId))
      }
      // Fallback for backward compatibility
      return Number(community.communityManagerId) === Number(this.$store.getters.userId)
    },
    viewCommunityActors (): void {
      this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
      this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, this.community.portfolioId)

      this.$router.push('/communities/' + this.community.id + '/actors')
    },
    viewCommunityMembers (): void {
      this.$router.push('/communities/' + this.community.id + '/members')
    },
    viewCommunityAnnouncements (): void {
      this.$router.push('/communities/' + this.community.id + '/announcements')
    },
    viewCommunityEvents (): void {
      this.$router.push('/communities/' + this.community.id + '/events')
    },
    requestAccessToTheCommunity (communityId: Number) {
      if (this.hasUserRequestedAccessToCommunity) {
        return
      }

      if (!this.isLoggedIn) {
        this.$router.push('/login?redirect_uri=/communities/' + this.communityId)
        return
      }

      requestAccessToCommunity(communityId)
        .then(() => {
          this.$store.dispatch(ACTION_TYPES.REFRESH_PROFILE)
        })
        .catch(error => {
          console.log(error)
        })
    },
  },
})
